import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { ReservationActivityUiModel } from '../../../../../shared/ui-models/reservation-activity.ui-model';
import { getReservationsActivities } from '../../../../shared/services/data.service';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';

export interface HistoryModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    reservationId: string;
  };
  eventHandlers: {
    onClose: () => void;
  };
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
  config: { showModal },
  data: { reservationId },
  eventHandlers: { onClose }
}) => {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<ReservationActivityUiModel[]>([]);

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [activities]);

  useEffect(() => {
    if (!showModal || !reservationId) return;

    setLoading(true);
    getReservationsActivities(reservationId)
      .then((response: ReservationActivityUiModel[] | ErrorInfo) => {
        if (!isErrorInfo(response)) {
          setActivities(response || []);
        }
      })
      .finally(() => setLoading(false));
  }, [showModal, reservationId]);

  return (
    <Modal
      config={{ showModal, fullScreen: false, showClose: true, maxWidth: 'sm' }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="flex flex-col h-[75vh] max-h-[690px]">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-[1.8rem] font-semibold text-gray-800">History</h2>
          <p className="text-[1.2rem] text-gray-400 mt-0.5">Reservation activity log</p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-[1.3rem]">
              Loading…
            </div>
          ) : sortedActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
              <img
                src={`${PUBLIC_PATH}/assets/svgs/no-data.svg`}
                alt="no history"
                className="w-24 opacity-50"
              />
              <span className="text-[1.3rem]">No history records</span>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedActivities.map((item) => (
                <div
                  key={`${item.action}-${item.date}-${item.username || ''}`}
                  className="relative"
                >
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[1.35rem] text-gray-800 font-semibold truncate">
                          {(item.action ? item.action.replace('_', ' ') : 'UNKNOWN ACTION') +
                            (item.name || item.username
                              ? ` • ${(item.name || item.username) as string}`
                              : '')}
                        </div>
                        <div className="text-[1.15rem] text-gray-400 mt-1">
                          {moment(item.date).format('MMM DD, YYYY HH:mm')}
                        </div>
                      </div>
                      <div className="w-2.5 h-2.5 rounded-full bg-[#566346] mt-2 flex-shrink-0" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
